import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { Card } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input, Select, Textarea } from "../components/ui/Fields";
import { ConfirmDialog } from "../components/ui/Modal";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";

/**
 * Round 49 — Admin EXP adjustment / reset for Charm, Wealth and Room Level
 * EXP (super_admin) over routes/admin/exp.js. No logic lives here: the
 * non-negative guarantee, locking, level calculation and audit logging are
 * all server-side (admin_adjust_xp, migration 0123). The values shown are
 * re-read from the server after every change.
 */
interface RoomInfo { id: number; roomId: string; title: string; roomXp: number; level: number; xpIntoLevel: number; xpForNextLevel: number }
interface FoundUser {
  id: string; userId: number; username: string; avatarUrl: string | null;
  charmXp: number; charmLevel: number; wealthXp: number; wealthLevel: number; room: RoomInfo | null;
}
type Kind = "charm" | "wealth" | "room";
type Mode = "add" | "deduct" | "reset";
interface Hist { id: number; kind: Kind; mode: Mode; delta: number; before_value: number; after_value: number; note: string; created_at: string }

const KIND_LABEL: Record<Kind, string> = { charm: "Charm EXP", wealth: "Wealth EXP", room: "Room Level EXP" };

export function ExpAdjustmentsPage() {
  const [target, setTarget] = useState<"user" | "room">("user");
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<FoundUser[]>([]);
  const [rooms, setRooms] = useState<RoomInfo[]>([]);
  const [selUser, setSelUser] = useState<FoundUser | null>(null);
  const [selRoom, setSelRoom] = useState<RoomInfo | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [kind, setKind] = useState<Kind>("charm");
  const [mode, setMode] = useState<Mode>("add");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState<Hist[]>([]);
  const { showToast } = useToast();

  async function loadHistory(u: FoundUser | null, r: RoomInfo | null) {
    try {
      const params = r && !u ? { roomId: String(r.id) } : u ? { userId: u.id } : null;
      if (!params) return;
      const res = await apiRequest<{ history: Hist[] }>("/admin/exp-adjustments/history", { params });
      setHistory(res.history);
    } catch { setHistory([]); }
  }

  async function search() {
    const q = query.trim();
    if (!q) return;
    setSearching(true); setError(null); setUsers([]); setRooms([]); setSelUser(null); setSelRoom(null); setHistory([]);
    try {
      if (target === "user") {
        const res = await apiRequest<{ results: FoundUser[] }>("/admin/exp-adjustments/lookup", { params: { query: q } });
        setUsers(res.results);
        if (res.results.length === 1) pickUser(res.results[0]);
      } else {
        const res = await apiRequest<{ results: RoomInfo[] }>("/admin/exp-adjustments/lookup-room", { params: { query: q } });
        setRooms(res.results);
        if (res.results.length === 1) pickRoom(res.results[0]);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Lookup failed");
    } finally {
      setSearching(false);
    }
  }

  function pickUser(u: FoundUser) { setSelUser(u); setSelRoom(null); setKind("charm"); void loadHistory(u, null); }
  function pickRoom(r: RoomInfo) { setSelRoom(r); setSelUser(null); setKind("room"); void loadHistory(null, r); }

  const current = (): number | null => {
    if (kind === "room") return (selRoom ?? selUser?.room)?.roomXp ?? null;
    if (!selUser) return null;
    return kind === "charm" ? selUser.charmXp : selUser.wealthXp;
  };
  const roomTarget = (): RoomInfo | null => selRoom ?? selUser?.room ?? null;
  const amountOk = mode === "reset" || (/^\d+$/.test(amount) && Number(amount) > 0);
  const canSubmit = amountOk && reason.trim().length > 0 && current() !== null;

  async function confirm() {
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = { kind, mode, note: reason.trim() };
      if (mode !== "reset") body.amount = Number(amount);
      if (kind === "room") body.roomId = roomTarget()?.id; else body.userId = selUser?.id;
      const res = await apiRequest<{ before: number; after: number; level: number }>("/admin/exp-adjustments", { method: "POST", body });
      showToast(`${KIND_LABEL[kind]}: ${res.before.toLocaleString()} → ${res.after.toLocaleString()} (Level ${res.level})`, "success");
      setConfirmOpen(false); setAmount(""); setReason("");
      // Re-read the authoritative values.
      if (selUser) {
        const fresh = await apiRequest<{ results: FoundUser[] }>("/admin/exp-adjustments/lookup", { params: { query: String(selUser.userId) } });
        const u = fresh.results.find((x) => x.id === selUser.id) ?? null;
        if (u) { setSelUser(u); void loadHistory(u, null); }
      } else if (selRoom) {
        const fresh = await apiRequest<{ results: RoomInfo[] }>("/admin/exp-adjustments/lookup-room", { params: { query: selRoom.roomId } });
        const r = fresh.results.find((x) => x.id === selRoom.id) ?? null;
        if (r) { setSelRoom(r); void loadHistory(null, r); }
      }
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not apply the change", "error");
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  const r = roomTarget();
  return (
    <AppShell title="EXP Adjustments">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        Add, deduct or reset Charm, Wealth or Room Level EXP. Changes are made on the server, can never take a value
        below zero, are recorded with your account and reason, and show the resulting level immediately. Reset sets
        the value to 0; new gifts keep adding on top afterwards.
      </p>
      <Card className="max-w-2xl p-5">
        <div className="flex items-end gap-2">
          <Select label="Find by" value={target} onChange={(e) => { setTarget(e.target.value as "user" | "room"); setUsers([]); setRooms([]); setSelUser(null); setSelRoom(null); setError(null); }}>
            <option value="user">User (Unique ID / username)</option>
            <option value="room">Room (Room ID)</option>
          </Select>
          <Input label={target === "user" ? "Unique ID or username" : "Room ID"} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void search(); }} />
          <Button variant="secondary" onClick={search} isLoading={searching} disabled={!query.trim()}>Find</Button>
        </div>
        {error && <p className="mt-1.5 text-xs text-danger">{error}</p>}

        {!selUser && !selRoom && (users.length > 1 || rooms.length > 1) && (
          <ul className="mt-3 divide-y divide-surface-border rounded border border-surface-border">
            {users.map((u) => (
              <li key={u.id}><button className="w-full px-3 py-2 text-left text-sm hover:bg-surface-raised" onClick={() => pickUser(u)}>{u.username} <span className="font-mono text-xs text-ink-faint">ID {u.userId}</span></button></li>
            ))}
            {rooms.map((x) => (
              <li key={x.id}><button className="w-full px-3 py-2 text-left text-sm hover:bg-surface-raised" onClick={() => pickRoom(x)}>{x.title} <span className="font-mono text-xs text-ink-faint">Room {x.roomId}</span></button></li>
            ))}
          </ul>
        )}

        {(selUser || selRoom) && (
          <div className="mt-4 space-y-4 border-t border-surface-border pt-4">
            {selUser && (
              <div>
                <p className="font-semibold text-ink">{selUser.username} <span className="font-mono text-xs text-ink-faint">ID {selUser.userId}</span></p>
                <div className="mt-2 grid grid-cols-3 gap-3 text-sm">
                  <div><p className="text-xs text-ink-faint">Charm EXP</p><p className="font-semibold">{selUser.charmXp.toLocaleString()} <span className="text-xs text-ink-faint">Lv {selUser.charmLevel}</span></p></div>
                  <div><p className="text-xs text-ink-faint">Wealth EXP</p><p className="font-semibold">{selUser.wealthXp.toLocaleString()} <span className="text-xs text-ink-faint">Lv {selUser.wealthLevel}</span></p></div>
                  <div><p className="text-xs text-ink-faint">Room Level EXP</p><p className="font-semibold">{selUser.room ? <>{selUser.room.roomXp.toLocaleString()} <span className="text-xs text-ink-faint">Lv {selUser.room.level}</span></> : <span className="text-ink-faint">no room</span>}</p></div>
                </div>
              </div>
            )}
            {selRoom && (
              <div>
                <p className="font-semibold text-ink">{selRoom.title} <span className="font-mono text-xs text-ink-faint">Room {selRoom.roomId}</span></p>
                <p className="mt-1 text-sm">Room Level EXP <span className="font-semibold">{selRoom.roomXp.toLocaleString()}</span> <span className="text-xs text-ink-faint">Lv {selRoom.level}</span></p>
              </div>
            )}

            {selUser && (
              <Select label="Value to change" value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
                <option value="charm">Charm EXP</option>
                <option value="wealth">Wealth EXP</option>
                <option value="room" disabled={!selUser.room}>Room Level EXP{selUser.room ? "" : " (user has no room)"}</option>
              </Select>
            )}
            <Select label="Action" value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
              <option value="add">Add</option>
              <option value="deduct">Deduct</option>
              <option value="reset">Reset to 0</option>
            </Select>
            {mode !== "reset" && (
              <Input label="Amount" type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} />
            )}
            <Textarea label="Reason (required — saved in the audit log)" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
            <Button variant={mode === "add" ? "primary" : "danger"} disabled={!canSubmit} onClick={() => setConfirmOpen(true)}>
              {mode === "add" ? "Add EXP" : mode === "deduct" ? "Deduct EXP" : "Reset EXP"}
            </Button>

            {history.length > 0 && (
              <div className="border-t border-surface-border pt-3">
                <p className="mb-1 text-xs font-semibold text-ink-faint">Recent adjustments</p>
                <ul className="space-y-1 text-xs text-ink-faint">
                  {history.map((h) => (
                    <li key={h.id}>{new Date(h.created_at).toLocaleString()} · {KIND_LABEL[h.kind]} · {h.mode} ({h.delta > 0 ? "+" : ""}{h.delta.toLocaleString()}) · {h.before_value.toLocaleString()} → {h.after_value.toLocaleString()} · {h.note}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => !submitting && setConfirmOpen(false)}
        onConfirm={confirm}
        isLoading={submitting}
        isDangerous={mode !== "add"}
        title="Confirm EXP change"
        confirmLabel={mode === "add" ? "Add" : mode === "deduct" ? "Deduct" : "Reset"}
        description={`${mode === "reset" ? "Reset" : mode === "add" ? `Add ${Number(amount).toLocaleString()} to` : `Deduct ${Number(amount).toLocaleString()} from`} ${KIND_LABEL[kind]} for ${kind === "room" ? `room "${r?.title ?? ""}"` : selUser?.username ?? ""}. Current value: ${(current() ?? 0).toLocaleString()}. Reason: "${reason.trim()}"`}
      />
    </AppShell>
  );
}
